<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ContactSortMenu from './components/ContactSortMenu.vue';
import ContactMoreActions from './components/ContactMoreActions.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';

defineProps({
  showSearch: { type: Boolean, default: true },
  searchValue: { type: String, default: '' },
  headerTitle: { type: String, required: true },
  buttonLabel: { type: String, default: '' },
  activeSort: { type: String, default: 'last_activity_at' },
  activeOrdering: { type: String, default: '' },
  isSegmentsView: { type: Boolean, default: false },
  hasActiveFilters: { type: Boolean, default: false },
  isLabelView: { type: Boolean, default: false },
  isActiveView: { type: Boolean, default: false },
  canManageActiveSegment: { type: Boolean, default: true },
});

const emit = defineEmits([
  'search',
  'filter',
  'update:sort',
  'add',
  'import',
  'export',
  'createSegment',
  'deleteSegment',
]);
</script>

<template>
  <header class="sticky top-0 z-20 px-6 bg-n-background">
    <div class="flex flex-col w-full gap-3 pt-6 pb-4 mx-auto max-w-[1240px]">
      <div
        class="flex items-start justify-between w-full gap-2 sm:items-center"
      >
        <span
          class="text-[21px] font-semibold tracking-[-0.02em] truncate text-n-slate-12"
        >
          {{ headerTitle }}
        </span>
        <div class="flex items-center flex-shrink-0 gap-2">
          <div
            v-if="
              !isLabelView &&
              !isActiveView &&
              (!isSegmentsView || canManageActiveSegment)
            "
            class="relative"
          >
            <Button
              id="toggleContactsFilterButton"
              :icon="
                isSegmentsView ? 'i-lucide-pen-line' : 'i-lucide-list-filter'
              "
              color="slate"
              size="sm"
              class="relative !h-[38px] !w-[38px] rounded-lg bg-n-solid-1 !outline-n-weak !text-n-slate-11"
              variant="ghost"
              @click="emit('filter')"
            >
              <div
                v-if="hasActiveFilters && !isSegmentsView"
                class="absolute top-0 right-0 w-2 h-2 rounded-full bg-n-blue-9"
              />
            </Button>
            <slot name="filter" />
          </div>
          <Button
            v-if="
              hasActiveFilters &&
              !isSegmentsView &&
              !isLabelView &&
              !isActiveView
            "
            icon="i-lucide-save"
            color="slate"
            size="sm"
            variant="ghost"
            class="!h-[38px] !w-[38px] rounded-lg bg-n-solid-1 !outline-n-weak !text-n-slate-11"
            @click="emit('createSegment')"
          />
          <Button
            v-if="
              isSegmentsView &&
              !isLabelView &&
              !isActiveView &&
              canManageActiveSegment
            "
            icon="i-lucide-trash"
            color="slate"
            size="sm"
            variant="ghost"
            class="!h-[38px] !w-[38px] rounded-lg bg-n-solid-1 !outline-n-weak !text-n-slate-11"
            @click="emit('deleteSegment')"
          />
          <Button
            v-if="
              isSegmentsView &&
              !isLabelView &&
              !isActiveView &&
              !canManageActiveSegment
            "
            v-tooltip.bottom-end="
              $t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.READ_ONLY_TOOLTIP'
              )
            "
            :aria-label="
              $t(
                'CONTACTS_LAYOUT.HEADER.ACTIONS.FILTERS.CREATE_SEGMENT.READ_ONLY_TOOLTIP'
              )
            "
            icon="i-lucide-info"
            color="slate"
            size="sm"
            variant="ghost"
            class="!h-[38px] !w-[38px] rounded-lg bg-n-solid-1 !outline-n-weak !text-n-slate-11"
          />
          <ContactSortMenu
            :active-sort="activeSort"
            :active-ordering="activeOrdering"
            @update:sort="emit('update:sort', $event)"
          />
          <ContactMoreActions
            @add="emit('add')"
            @import="emit('import')"
            @export="emit('export')"
          />
          <ComposeConversation>
            <template #trigger>
              <Button
                :label="buttonLabel"
                icon="i-lucide-plus"
                size="sm"
                class="!h-[38px] rounded-lg !text-[13px] font-semibold !bg-n-blue-9 hover:enabled:!bg-n-blue-10 !text-white ltr:ml-1 rtl:mr-1"
              />
            </template>
          </ComposeConversation>
        </div>
      </div>
      <div v-if="showSearch" class="flex items-center w-full gap-2.5">
        <Input
          :model-value="searchValue"
          type="search"
          :placeholder="$t('CONTACTS_LAYOUT.HEADER.SEARCH_PLACEHOLDER')"
          :custom-input-class="[
            'h-10 !bg-n-solid-1 rounded-lg !text-[13.5px] ltr:!pl-9 rtl:!pr-9',
          ]"
          class="w-full sm:max-w-[420px]"
          @input="emit('search', $event.target.value)"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-10 size-4 top-1/2 ltr:left-3 rtl:right-3"
            />
          </template>
        </Input>
      </div>
    </div>
  </header>
</template>
